import firebase from 'firebase/compat/app'
import { useEffect } from 'react'
import { format } from 'timeago.js'

import { F } from '../context'
import { DataCollectionRecord, assetsId, profilesId } from '../services/cms'
import {
  Action,
  ActionType,
  AnyVariable,
  AudioVariable,
  BrandingColorType,
  BrandingThemeType,
  Collection,
  CollectionProperty,
  CollectionVariable,
  Config,
  DataObject,
  DateVariable,
  InputParameter,
  LocalVariable,
  LocalizedContent,
  NEED_INDEX,
  NumberVariable,
  RecordModification,
  Screen,
  ScreenComponent,
  ScreenParameter,
  TableQuery,
  TableQueryFilterOperator,
  TableQueryOrder,
  ValueType,
  VariableSource,
  VariableSourceType,
  VariableTransform,
  VariableTransformTransform,
  VideoVariable,
  collectionNameToFind,
  convertRecord,
  findComponentByFieldName,
  generateFirestoreId,
  getCollectionProperties,
  getNumberValue,
  getTextValue,
  getValueType,
  needOrderByOperators,
  operators,
  queryOperators,
  reconvertRecord,
  throttleError,
  toast,
} from '../utils'

export type Refresh = { [key: string]: () => void }

type Throttle = { wait: true; valueChanged?: true }

export type GetVariable = (screenConfig: Screen, listId?: string, indexInList?: number) => GetVariableValue

export type GetVariableValue = (
  variable?: AnyVariable,
  refresh?: Refresh,
  toSet?: { value: any },
  initialSet?: boolean,
  listParams?: { listId: string; listItemContextKey: string; limit?: number },
  checkIfVariableExists?: boolean,
  realRecord?: DataCollectionRecord,
  refreshAdded?: boolean
) => Promise<any>

export type Update = (getVariableValue: GetVariableValue, action: Action) => Promise<void>

export interface CollectionWithRecords extends Collection {
  collectionId: string
  recordId?: string
  records: DataCollectionRecord[]
  queryString?: string
  limit?: number
}

export interface AssetsCollectionWithRecords extends Collection {
  records: DataCollectionRecord[]
}

export interface DataObjectWithRecords extends DataObject {
  records: DataCollectionRecord[]
}

export let scrolls: { [key: string]: () => void } = {}
export let chatsLimits: { [key: string]: number } = {}
let refreshes: { [key: string]: Refresh[] } = {}
let throttles: { [key: string]: Throttle } = {}

const componentVariableKey = 'component'
let variables: { [key: string]: any } = {}
let firebaseDatasources: { [key: string]: DataCollectionRecord } = {}
const snapshots: { [key: string]: () => void } = {}

export const deleteScreenComponentsVariables = (screenId: string) =>
  Object.keys(variables).forEach(el => el.startsWith(`${componentVariableKey}/${screenId}/`) && delete variables[el])

export const generateRefreshKey = (id: string, name: string, indexInList?: number) =>
  `${id}/${name}${indexInList !== undefined ? `/${indexInList}` : ''}`

const addRefresh = (name: string, refresh?: Refresh, refreshAdded?: boolean) =>
  refresh &&
  !refreshAdded &&
  (refreshes[name] = [...(refreshes[name] || []).filter(el => Object.keys(el)[0] !== Object.keys(refresh)[0]), refresh])

const runRefreshes = (name: string) => {
  if (refreshes[name]) {
    let length = refreshes[name].length
    for (let i = 0; i < length; i++) {
      const refresh = refreshes[name][i]
      refreshes[name].splice(i, 1)
      i--
      length--
      refresh[Object.keys(refresh)[0]]()
    }
  }
}

export const useVariable = (
  f: F,
  inDevice: boolean,
  desktopMode: boolean,
  config: Config,
  language: string,
  theme: BrandingThemeType,
  screen?: Screen,
  inputParameterValue?: string,
  searchParams?: URLSearchParams
) => {
  const {
    data: { collections = [], dataObjects = [], globalVariables = [] },
    branding: {
      appName,
      icons: { iosIcon },
      colorStyles,
      constants,
    },
    resources = [],
    tabBars = [],
  } = config
  const applicationName = appName.locales[language]
  const applicationIcon = resources.find(el => el.id === iosIcon)
  const { firestorePrefix, auth, firestore, getRemoteConfigValue, search, getAssetRecord } = f

  const getVariable: GetVariable = (screenConfig, listId, indexInList) => {
    const getVariableValue: GetVariableValue = async (
      variable,
      refresh,
      toSet,
      initialSet,
      listParams,
      checkIfVariableExists,
      realRecord,
      refreshAdded
    ) => {
      if (variable) {
        const getVariableValue = getVariable(screenConfig, listId, indexInList)
        const {
          source,
          textConstant,
          numberConstant,
          booleanConstant,
          calendarStyleConstant,
          resourceTypeConstant,
          colorConstant,
          currencyConstant,
          durationConstant,
          urlConstant,
          imageConstant,
          videoConstant,
          audioConstant,
          fileConstant,
          dateConstant,
          dateTimeConstant,
          accessLevelConstant,
          arrayConstant,
          stringConstant,
        } = variable
        const screenId = screenConfig.id
        if (source !== undefined) {
          const { type, collection, fieldName, selector, query, variableName, componentName, transforms } = source
          switch (type) {
            case VariableSourceType.globalVariable:
              if (variableName) {
                let value = null
                switch (variableName) {
                  case 'currentUserId':
                    value = auth.currentUser?.uid
                    break
                  case 'applicationVersion':
                    value = 'v.1.0.1 (1)'
                    break
                  case 'applicationName':
                    value = applicationName
                    break
                  case 'applicationIcon':
                    value = applicationIcon
                    break
                  case 'isSignedOut':
                    value = !auth.currentUser
                    break
                  case 'isAnonymous':
                    value = !!auth.currentUser?.isAnonymous
                    break
                  case 'hasUserAccount':
                    value = !!auth.currentUser && !auth.currentUser.isAnonymous
                    break
                  case 'isEmailVerified':
                    value = !!auth.currentUser?.emailVerified
                    break
                  default:
                    const globalVariable = globalVariables.find(el => el.variableName === variableName)
                    if (globalVariable) {
                      value = await getVariableValue(globalVariable.variable, refresh)
                    }
                }
                return transformValue(value, getVariableValue, refresh, transforms)
              }
              return transformValue(null, getVariableValue, refresh, transforms)
            case VariableSourceType.remoteConfig:
              if (variableName) {
                const value = getRemoteConfigValue(variableName)
                return transformValue(value, getVariableValue, refresh, transforms)
              }
              return transformValue(null, getVariableValue, refresh, transforms)
            case VariableSourceType.collection:
              if (collection) {
                const collectionId = await getCurrentCollectionId(collection, getVariableValue, refresh)
                const collectionData = collections.find(el => el.name === collection.name)
                if (collectionData) {
                  const { properties = [] } = collectionData
                  const recordId = realRecord
                    ? realRecord.id
                    : await getVariableValue({ ...selector, stringConstant: selector?.constant }, refresh)
                  const getFirebaseDatasourceRecord = async (recordId: string) => {
                    if (realRecord) {
                      return realRecord
                    } else {
                      const firebaseDatasourceName = `${collectionId}/${recordId}`
                      addRefresh(firebaseDatasourceName, refresh, refreshAdded)
                      if (!firebaseDatasources[firebaseDatasourceName]) {
                        const set = (record: DataCollectionRecord) => {
                          firebaseDatasources[firebaseDatasourceName] = record
                        }
                        await getRecord(set, collectionId, recordId)
                      }
                      return firebaseDatasources[firebaseDatasourceName]
                    }
                  }
                  if (listParams) {
                    const { listId, listItemContextKey, limit } = listParams
                    const name = `${screenId}/${listId}/${listItemContextKey}${indexInList !== undefined ? `/${indexInList}` : ''}`
                    addRefresh(name, refresh, refreshAdded)
                    const queryString = await queryToString(name, getVariableValue, query)
                    const variable = variables[name]
                    if (
                      !variable ||
                      (typeof variable === 'object' &&
                        (variable.collectionId !== collectionId ||
                          variable.recordId !== recordId ||
                          variable.queryString !== queryString ||
                          variable.limit !== limit))
                    ) {
                      if (recordId && fieldName) {
                        const record = await getFirebaseDatasourceRecord(recordId)
                        if (record[fieldName]) {
                          const property = properties.find(el => el.name === fieldName)
                          const subCollection =
                            property &&
                            property.type === ValueType.array &&
                            property.accept === ValueType.record &&
                            property.collection &&
                            collections.find(el => el.name === property.collection)
                          const assets =
                            property &&
                            property.type === ValueType.array &&
                            (property.accept === ValueType.image ||
                              property.accept === ValueType.video ||
                              property.accept === ValueType.audio ||
                              property.accept === ValueType.file) &&
                            collections.find(el => el.name === assetsId)
                          const dataObject =
                            property &&
                            property.type === ValueType.array &&
                            property.accept === ValueType.keyValueMap &&
                            property.dataObject &&
                            dataObjects.find(el => el.name === property.dataObject)
                          if (
                            subCollection &&
                            (!variable ||
                              (typeof variable === 'object' &&
                                (variable.collectionId !== subCollection.name ||
                                  variable.recordId !== recordId ||
                                  variable.queryString !== queryString ||
                                  variable.limit !== limit)))
                          ) {
                            const recordsIds = record[fieldName].map((el: string) => extractRecordRef(el).recordId)
                            if (recordsIds.length) {
                              const set = (records: DataCollectionRecord[]) => {
                                records.sort((a, b) => (recordsIds.indexOf(a.id) > recordsIds.indexOf(b.id) ? 1 : -1))
                                variables[name] = {
                                  ...subCollection,
                                  collectionId: subCollection.name,
                                  recordId,
                                  records,
                                  queryString,
                                  limit,
                                }
                              }
                              // TODO: subCollection.name -> collectionId
                              await getRecords(name, set, subCollection.name, queryString, limit, recordsIds)
                            }
                          } else if (assets) {
                            variables[name] = {
                              ...assets,
                              records: record[fieldName],
                            }
                          } else if (dataObject) {
                            variables[name] = {
                              ...dataObject,
                              records: record[fieldName],
                            }
                          }
                        }
                      } else {
                        const set = (records: DataCollectionRecord[]) => {
                          variables[name] = {
                            ...collectionData,
                            collectionId,
                            recordId,
                            records,
                            queryString,
                            limit,
                          }
                        }
                        await getRecords(name, set, collectionId, queryString, limit)
                      }
                    }
                    return variables[name]
                  } else if (recordId) {
                    if (fieldName || transforms?.length) {
                      const record = await getFirebaseDatasourceRecord(recordId)
                      return transformValue(
                        record,
                        getVariableValue,
                        refresh,
                        fieldName
                          ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                          : transforms,
                        properties,
                        collectionId
                      )
                    } else if (initialSet) {
                      const record = await getFirebaseDatasourceRecord(recordId)
                      return {
                        ...collectionData,
                        collectionId,
                        records: [record],
                      }
                    } else if (collectionId === assetsId) {
                      const record = await getFirebaseDatasourceRecord(recordId)
                      return record
                    } else {
                      return `${collectionPrefix}${collectionId}/${recordId}`
                    }
                  } else if (transforms?.[0]?.transform === VariableTransformTransform.first) {
                    const name = `${screenId}${listId !== undefined ? `/${listId}` : ''}${indexInList !== undefined ? `/${indexInList}` : ''}/${collectionId}${query ? `/${JSON.stringify(query)}` : ''}/${transforms[0].transform}`
                    addRefresh(name, refresh, refreshAdded)
                    const queryString = await queryToString(name, getVariableValue, query)
                    const variable = variables[name]
                    if (
                      !variable ||
                      (typeof variable === 'object' &&
                        (variable.collectionId !== collectionId || variable.queryString !== queryString))
                    ) {
                      const set = (records: DataCollectionRecord[]) => {
                        variables[name] = {
                          ...collectionData,
                          collectionId,
                          records,
                          queryString,
                        }
                      }
                      await getRecords(name, set, collectionId, queryString, 1)
                    }
                    const nextTransforms = transforms.slice(1)
                    if (fieldName || nextTransforms.length) {
                      const record = variables[name].records[0] || {}
                      return transformValue(
                        record,
                        getVariableValue,
                        refresh,
                        fieldName
                          ? [{ transform: VariableTransformTransform.field, fieldName }, ...nextTransforms]
                          : nextTransforms,
                        properties,
                        collectionId
                      )
                    } else if (initialSet) {
                      const record = variables[name].records[0] || {}
                      return {
                        ...collectionData,
                        collectionId,
                        records: [record],
                      }
                    } else if (collectionId === assetsId) {
                      const record = variables[name].records[0] || {}
                      return record
                    } else {
                      const record = variables[name].records[0] || {}
                      return `${collectionPrefix}${collectionId}/${record.id}`
                    }
                  } else if (fieldName === 'count') {
                    const name = `${screenId}${listId !== undefined ? `/${listId}` : ''}${indexInList !== undefined ? `/${indexInList}` : ''}/${collectionId}${query ? `/${JSON.stringify(query)}` : ''}/${fieldName}`
                    addRefresh(name, refresh, refreshAdded)
                    const queryString = await queryToString(name, getVariableValue, query)
                    const variable = variables[name]
                    if (
                      !variable ||
                      (typeof variable === 'object' &&
                        (variable.collectionId !== collectionId || variable.queryString !== queryString))
                    ) {
                      const set = (records: DataCollectionRecord[]) => {
                        variables[name] = {
                          ...collectionData,
                          collectionId,
                          records,
                          queryString,
                        }
                      }
                      await getRecords(name, set, collectionId, queryString)
                    }
                    const value = variables[name].records
                    return transformValue(
                      value,
                      getVariableValue,
                      refresh,
                      fieldName
                        ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                        : transforms
                    )
                  } else {
                    return transformValue(
                      null,
                      getVariableValue,
                      refresh,
                      fieldName
                        ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                        : transforms,
                      properties,
                      collectionId
                    )
                  }
                }
              }
              return transformValue(
                null,
                getVariableValue,
                refresh,
                fieldName
                  ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                  : transforms
              )
            case VariableSourceType.localVariable:
              if (variableName) {
                const name1 = `${screenId}/${listId}/${variableName}/${indexInList}`
                const name2 = `${screenId}/${listId}/${variableName}`
                const name3 = `${screenId}/${variableName}`
                const innerListItemValue = variables[name1]
                const listItemsValue = variables[name2]
                if (toSet !== undefined) {
                  let name
                  if (initialSet) {
                    name = listId && indexInList !== undefined ? name1 : listId ? name2 : name3
                  } else {
                    name = innerListItemValue !== undefined ? name1 : listItemsValue !== undefined ? name2 : name3
                  }
                  if (JSON.stringify(variables[name]) !== JSON.stringify(toSet.value)) {
                    variables[name] = toSet.value
                    runRefreshes(name)
                  }
                  return variables[name]
                }
                const name = innerListItemValue !== undefined ? name1 : listItemsValue !== undefined ? name2 : name3
                const localRefresh = innerListItemValue || !listItemsValue
                if (localRefresh) {
                  addRefresh(name, refresh, refreshAdded)
                }
                const variable = variables[name]
                if (
                  variable &&
                  typeof variable === 'object' &&
                  variable.name &&
                  variable.collectionId &&
                  variable.records
                ) {
                  const { name, collectionId, records } = variable as CollectionWithRecords
                  const record =
                    innerListItemValue !== undefined
                      ? records[0] || {}
                      : listItemsValue !== undefined
                        ? records[indexInList || 0] || {}
                        : records[0] || {}
                  return getVariableValue(
                    {
                      source: {
                        type: VariableSourceType.collection,
                        collection: { name, collectionId },
                        fieldName,
                        query,
                        transforms,
                      },
                    },
                    refresh,
                    toSet,
                    initialSet,
                    listParams,
                    checkIfVariableExists,
                    record,
                    localRefresh
                  )
                } else if (variable && typeof variable === 'object' && variable.name && variable.records) {
                  const { records, properties } = variable as AssetsCollectionWithRecords | DataObjectWithRecords
                  const record =
                    innerListItemValue !== undefined
                      ? records[0] || {}
                      : listItemsValue !== undefined
                        ? records[indexInList || 0] || {}
                        : records[0] || {}
                  return transformValue(
                    record,
                    getVariableValue,
                    refresh,
                    fieldName
                      ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                      : transforms,
                    properties
                  )
                } else {
                  return transformValue(
                    variable,
                    getVariableValue,
                    refresh,
                    fieldName
                      ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                      : transforms
                  )
                }
              }
              return transformValue(
                null,
                getVariableValue,
                refresh,
                fieldName
                  ? [{ transform: VariableTransformTransform.field, fieldName }, ...(transforms || [])]
                  : transforms
              )
            case VariableSourceType.component:
              if (componentName && fieldName) {
                const name = `${componentVariableKey}/${screenId}${listId !== undefined ? `/${listId}` : ''}${indexInList !== undefined ? `/${indexInList}` : ''}/${componentName}/${fieldName}`
                if (checkIfVariableExists) {
                  return variables[name] !== undefined
                }
                if (toSet !== undefined) {
                  if (JSON.stringify(variables[name]) !== JSON.stringify(toSet.value)) {
                    if (fieldName === 'video') {
                      const name = `${componentVariableKey}/${screenId}/${componentName}/value`
                      variables[name] = toSet.value?.startTime || 0
                    }
                    variables[name] = toSet.value
                    runRefreshes(name)
                  }
                  return variables[name]
                }
                addRefresh(name, refresh, refreshAdded)
                let variable = variables[name]
                if (variable === undefined) {
                  const component = findComponentByFieldName(screenConfig, 'name', componentName)
                  if (component) {
                    const fieldValue = component[fieldName as keyof ScreenComponent]
                    if (fieldName === 'text') {
                      const text = fieldValue as LocalizedContent | undefined
                      variable = await getVariableValue({ textConstant: text }, refresh)
                    } else if (fieldName === 'date' || fieldName === 'displayDate') {
                      const date = fieldValue as DateVariable | undefined
                      variable = await getVariableValue({ ...date, dateConstant: date?.constant }, refresh)
                    } else if (fieldName === 'value') {
                      const value = fieldValue as NumberVariable | undefined
                      variable = await getVariableValue({ ...value, numberConstant: value?.constant }, refresh)
                    } else if (fieldName === 'video') {
                      const value = fieldValue as VideoVariable | undefined
                      variable = await getVariableValue({ ...value, videoConstant: value?.constant }, refresh)
                    } else if (fieldName === 'audio') {
                      const value = fieldValue as AudioVariable | undefined
                      variable = await getVariableValue({ ...value, audioConstant: value?.constant }, refresh)
                    } else if (fieldName === 'offer') {
                      const offer = fieldValue as VariableSource | undefined
                      variable = await getVariableValue({ source: offer }, refresh)
                    } else if (fieldName === 'visibleItem') {
                      const {
                        id,
                        listItems,
                        listItemContextKey,
                        listId: parentListId,
                        indexInList: parentIndexInList,
                      } = component
                      const listId =
                        parentListId && parentIndexInList !== undefined
                          ? `${parentListId}${parentIndexInList}${id}`
                          : id
                      if (listItemContextKey) {
                        const listItemsValue = await getVariableValue(listItems, refresh, undefined, undefined, {
                          listId,
                          listItemContextKey,
                        })
                        const valueValue = await getVariableValue(
                          { source: { type: VariableSourceType.component, componentName, fieldName: 'value' } },
                          refresh
                        )
                        if (
                          listItemsValue &&
                          typeof listItemsValue === 'object' &&
                          listItemsValue.name &&
                          listItemsValue.collectionId &&
                          listItemsValue.records &&
                          typeof valueValue === 'number'
                        ) {
                          const { collectionId, records } = listItemsValue as CollectionWithRecords
                          if (records[valueValue]) {
                            const recordId = records[valueValue].id
                            variable = `${collectionPrefix}${collectionId}/${recordId}`
                          }
                        }
                      }
                    }
                  }
                }
                return transformValue(variable, getVariableValue, refresh, transforms)
              }
              return transformValue(null, getVariableValue, refresh, transforms)
            default:
              return transformValue(null, getVariableValue, refresh, transforms)
          }
        } else if (textConstant !== undefined) {
          return getTextValue(language, getVariableValue, refresh, textConstant)
        } else if (numberConstant !== undefined) {
          const { screenName, showTopBar } = screenConfig
          const tabBar = tabBars.find(el => el.tabs?.find(el => el.screen === screenName))
          const showTopBarValue = await getVariableValue({ ...showTopBar, booleanConstant: showTopBar?.constant })
          return getNumberValue(constants, numberConstant, showTopBarValue, !!tabBar, desktopMode, inDevice)
        } else if (booleanConstant !== undefined) {
          return booleanConstant
        } else if (calendarStyleConstant !== undefined) {
          return calendarStyleConstant
        } else if (resourceTypeConstant !== undefined) {
          return resourceTypeConstant
        } else if (colorConstant !== undefined) {
          const color = colorStyles[colorConstant?.slice(1) as BrandingColorType]
          return color ? color[theme] : colorConstant
        } else if (currencyConstant !== undefined) {
          return currencyConstant
        } else if (durationConstant !== undefined) {
          return durationConstant
        } else if (urlConstant !== undefined) {
          return urlConstant
        } else if (imageConstant !== undefined) {
          return resources.find(el => el.id === imageConstant.resourceId)
        } else if (videoConstant !== undefined) {
          return resources.find(el => el.id === videoConstant.resourceId)
        } else if (audioConstant !== undefined) {
          return resources.find(el => el.id === audioConstant.resourceId)
        } else if (fileConstant !== undefined) {
          return resources.find(el => el.id === fileConstant.resourceId)
        } else if (dateConstant !== undefined) {
          return dateConstant === '@today' ? new Date().toISOString() : dateConstant
        } else if (dateTimeConstant !== undefined) {
          return dateTimeConstant === '@now' ? new Date().toISOString() : dateTimeConstant
        } else if (accessLevelConstant !== undefined) {
          return accessLevelConstant
        } else if (arrayConstant !== undefined) {
          const value = []
          for (const el of arrayConstant) {
            value.push(await getVariableValue(el, refresh))
          }
          if (listParams) {
            const { listId, listItemContextKey } = listParams
            const name = `${screenId}/${listId}/${listItemContextKey}${indexInList !== undefined ? `/${indexInList}` : ''}`
            variables[name] = {
              records: value,
            }
            return variables[name]
          }
          return value
        } else if (stringConstant !== undefined) {
          return stringConstant
        }
        return null
      }
      return null
    }
    return getVariableValue
  }

  const transformValue = async (
    value: any,
    getVariableValue: GetVariableValue,
    refresh?: Refresh,
    transforms?: VariableTransform[],
    properties?: CollectionProperty[],
    collectionId?: string
  ): Promise<any> => {
    if (transforms) {
      for (let i = 0; i < transforms.length; i++) {
        const {
          transform,
          fieldName,
          paramName,
          participantsKey,
          participantNameKeys,
          participantImageKey,
          value: transformValue1,
          value2: transformValue2,
          type: transformType,
        } = transforms[i]
        const value1 = await getVariableValue(transformValue1, refresh)
        const value2 = await getVariableValue(transformValue2, refresh)
        switch (transform) {
          case VariableTransformTransform.matchesRegexp:
            if (typeof value === 'string' && typeof value1 === 'string') {
              value = new RegExp(value1).test(value)
            }
            break
          case VariableTransformTransform.formatAsCurrency:
            if (typeof value === 'number' && typeof value1 === 'string') {
              value = new Intl.NumberFormat(language, { style: 'currency', currency: value1 }).format(value)
            }
            break
          case VariableTransformTransform.contains:
            if (typeof value === 'string' || Array.isArray(value)) {
              value = value.includes(value1)
            }
            break
          case VariableTransformTransform.throttle:
            if (typeof value1 === 'number' && refresh) {
              const key = Object.keys(refresh)[0]
              if (!throttles[key]) {
                throttles[key] = { wait: true }
                // eslint-disable-next-line
                setTimeout(() => {
                  if (throttles[key]) {
                    const { valueChanged } = throttles[key]
                    delete throttles[key]
                    if (valueChanged) {
                      refresh[key]()
                    }
                  }
                }, value1 * 1000)
              } else {
                throttles[key] = { ...throttles[key], valueChanged: true }
                throw new Error(throttleError)
              }
            }
            break
          case VariableTransformTransform.trimWhitespacesAndNewlines:
            if (typeof value === 'string') {
              value = value.trim()
            }
            break
          case VariableTransformTransform.capitalizeFirstLetter:
            if (typeof value === 'string' && value[0]) {
              value = value[0].toUpperCase() + value.slice(1)
            }
            break
          case VariableTransformTransform.offer:
            if (value?.stripeId !== undefined) {
              value = value.stripeId
            } else {
              value = null
            }
            break
          case VariableTransformTransform.identifier:
            if (value?.id !== undefined) {
              value = value.id
            } else {
              value = null
            }
            break
          case VariableTransformTransform.exists:
            value = value !== null && value !== undefined && JSON.stringify(value) !== '{}'
            break
          case VariableTransformTransform.isNotEmpty:
            value = value !== null && value !== undefined && JSON.stringify(value) !== '{}' && value.length !== 0
            break
          case VariableTransformTransform.isEmpty:
            value = value === null || value === undefined || JSON.stringify(value) === '{}' || value.length === 0
            break
          case VariableTransformTransform.boolNot:
            value = !value
            break
          case VariableTransformTransform.anyTrue:
            if (Array.isArray(value)) {
              value = value.some((el: any) => !!el)
            }
            break
          case VariableTransformTransform.allTrue:
            if (Array.isArray(value)) {
              value = value.every((el: any) => !!el)
            }
            break
          case VariableTransformTransform.indexOf:
            if (Array.isArray(value)) {
              const index = value.indexOf(value1)
              value = index !== -1 ? index : null
            }
            break
          case VariableTransformTransform.atIndex:
            if (Array.isArray(value) && typeof value1 === 'number') {
              const isIndexIn = value1 >= 0 && value1 < value.length
              value = isIndexIn ? value[value1] : null
              if (value !== null && value !== undefined) {
                const prevTransform = transforms[i - 1]
                const isPrevFieldTransform = prevTransform?.transform === VariableTransformTransform.field
                if (isPrevFieldTransform) {
                  const property = properties?.find(el => el.name === prevTransform.fieldName)
                  const type = property?.type
                  const accept = property?.accept
                  const collection = property?.collection
                  const dataObject = property?.dataObject
                  if (type === ValueType.array && accept === ValueType.record && collection) {
                    return transformValue(
                      { value },
                      getVariableValue,
                      refresh,
                      [{ transform: VariableTransformTransform.field, fieldName: 'value' }, ...transforms.slice(i + 1)],
                      [{ position: 0, name: 'value', type: ValueType.record, collection }]
                    )
                  } else if (type === ValueType.array && accept === ValueType.keyValueMap && dataObject) {
                    return transformValue(
                      { value },
                      getVariableValue,
                      refresh,
                      [{ transform: VariableTransformTransform.field, fieldName: 'value' }, ...transforms.slice(i + 1)],
                      [{ position: 0, name: 'value', type: ValueType.keyValueMap, dataObject }]
                    )
                  } else if (type === ValueType.array && accept === ValueType.color) {
                    value = await getVariableValue({ colorConstant: value }, refresh)
                  }
                }
              } else {
                value = null
              }
            }
            break
          case VariableTransformTransform.conditionalValue:
            value = value ? value1 : value2
            break
          case VariableTransformTransform.formatTimeAgo:
            if (typeof value === 'string') {
              value = format(value)
            }
            break
          case VariableTransformTransform.formatTimeInterval:
            if (typeof value === 'number') {
              const fullTime = new Date(value * 1000).toISOString().slice(11, 19)
              value = fullTime.startsWith('00:')
                ? fullTime.slice(3, 8).startsWith('0')
                  ? fullTime.slice(4, 8)
                  : fullTime.slice(3, 8)
                : fullTime.startsWith('0')
                  ? fullTime.slice(1)
                  : fullTime
            }
            break
          case VariableTransformTransform.compareEqual:
          case VariableTransformTransform.compareNotEqual:
          case VariableTransformTransform.compareGreater:
          case VariableTransformTransform.compareLess:
          case VariableTransformTransform.compareGreaterOrEqual:
          case VariableTransformTransform.compareLessOrEqual:
            value =
              transform === VariableTransformTransform.compareEqual
                ? Array.isArray(value)
                  ? JSON.stringify(value) === JSON.stringify(value1)
                  : value === value1
                : transform === VariableTransformTransform.compareNotEqual
                  ? value !== value1
                  : transform === VariableTransformTransform.compareGreater
                    ? value > value1
                    : transform === VariableTransformTransform.compareLess
                      ? value < value1
                      : transform === VariableTransformTransform.compareGreaterOrEqual
                        ? value >= value1
                        : value <= value1
            break
          case VariableTransformTransform.round:
            if (typeof value === 'number' && typeof value1 === 'number') {
              if (value1 >= 0) {
                return Number(value).toFixed(value1)
              } else {
                const factor = Math.pow(10, -value1)
                return Math.round(Number(value) / factor) * factor
              }
            } else if (typeof value === 'number' && (value1 === null || value1 === undefined)) {
              return Number(value).toFixed()
            } else {
              value = null
            }
            break
          case VariableTransformTransform.add:
          case VariableTransformTransform.sub:
          case VariableTransformTransform.mult:
          case VariableTransformTransform.div:
            if (typeof value === 'number' && typeof value1 === 'number') {
              value =
                transform === VariableTransformTransform.add
                  ? value + value1
                  : transform === VariableTransformTransform.sub
                    ? value - value1
                    : transform === VariableTransformTransform.mult
                      ? value * value1
                      : value / value1
            } else if (Array.isArray(value)) {
              value =
                transform === VariableTransformTransform.add
                  ? value.filter(el => el !== value1).push(value1)
                  : transform === VariableTransformTransform.sub
                    ? value.filter(el => el !== value1)
                    : value
            } else {
              value = null
            }
            break
          case VariableTransformTransform.cast:
            if (transformType === ValueType.string) {
              value = String(value)
            } else if (transformType === ValueType.number) {
              value = Number(value)
            } else if (transformType === ValueType.boolean) {
              value = Boolean(value)
            }
            break
          case VariableTransformTransform.field:
            if (fieldName) {
              const property = i === 0 ? properties?.find(el => el.name === fieldName) : undefined
              const type = property?.type
              const collection = property?.collection
              const dataObject = property?.dataObject
              if (value?.[fieldName] !== null && value?.[fieldName] !== undefined) {
                if (type === ValueType.record && collection) {
                  const { collectionId, recordId } = extractRecordRef(value[fieldName])
                  return getVariableValue(
                    {
                      source: {
                        type: VariableSourceType.collection,
                        collection: { name: collection, collectionId },
                        selector: { constant: recordId },
                        transforms: transforms.slice(i + 1),
                      },
                    },
                    refresh
                  )
                } else if (type === ValueType.keyValueMap && dataObject) {
                  value = value[fieldName]
                  const dataObjectProperties = dataObjects.find(el => el.name === dataObject)?.properties
                  return transformValue(value, getVariableValue, refresh, transforms.slice(i + 1), dataObjectProperties)
                } else if (type === ValueType.color) {
                  value = await getVariableValue({ colorConstant: value[fieldName] }, refresh)
                } else {
                  value = value[fieldName]
                }
              } else if (Array.isArray(value) && fieldName === 'count') {
                value = value.length
              } else {
                value = null
              }
            }
            break
          case VariableTransformTransform.conversationTitle:
          case VariableTransformTransform.conversationImage:
            if (fieldName && value?.[fieldName]) {
              value = value[fieldName]
            } else if (
              value &&
              participantsKey &&
              value[participantsKey] &&
              value[participantsKey].length === 2 &&
              participantNameKeys
            ) {
              const currentUserId = await getVariableValue(
                { source: { type: VariableSourceType.globalVariable, variableName: 'currentUserId' } },
                refresh
              )
              const participantId = value[participantsKey].find((el: string) => el !== currentUserId)
              if (transform === VariableTransformTransform.conversationTitle) {
                value = await getParticipantFullName(participantId, participantNameKeys, getVariableValue, refresh)
              } else if (participantImageKey) {
                value = await getVariableValue(
                  {
                    source: {
                      type: VariableSourceType.collection,
                      collection: { name: profilesId },
                      selector: { constant: participantId },
                      fieldName: participantImageKey,
                    },
                  },
                  refresh
                )
                if (!value) {
                  value = await createAvatar(participantId, participantNameKeys, getVariableValue, refresh)
                }
              }
            } else {
              value = null
            }
            break
          case VariableTransformTransform.first:
            if (Array.isArray(value)) {
              if (value[0]) {
                value = value[0]
              } else {
                value = null
              }
            }
            break
          case VariableTransformTransform.collectionParam:
            if (collectionId && paramName) {
              const collecitonName = collections.find(
                el => collectionNameToFind(el.name) === collectionNameToFind(collectionId)
              )?.name
              if (collecitonName) {
                const a = collectionId.split('/').filter((el, i) => i % 2)
                const b = collecitonName
                  .split('/')
                  .filter((el, i) => i % 2)
                  .map(el => el.slice(1, -1))
                const index = b.indexOf(paramName)
                if (index !== -1) {
                  value = a[index]
                } else {
                  value = null
                }
              } else {
                value = null
              }
            } else {
              value = null
            }
            break
          case VariableTransformTransform.withField:
            if (value && typeof value === 'object' && fieldName) {
              value[fieldName] = value1
            }
            break
        }
      }
    }
    return value
  }

  const getRecord = (set: (record: DataCollectionRecord) => void, collectionId: string, recordId: string) =>
    new Promise(resolve => {
      const properties = getCollectionProperties(collections, collectionId)
      const assetsProperties = getCollectionProperties(collections, assetsId)
      const firebaseDatasourceName = `${collectionId}/${recordId}`
      let loaded = false
      const setRecord = async (record: DataCollectionRecord) => {
        await set(record)
        setTimeout(() => {
          if (loaded) {
            runRefreshes(firebaseDatasourceName)
          }
          loaded = true
          resolve(null)
        }, 1)
      }
      snapshots[firebaseDatasourceName]?.()
      snapshots[firebaseDatasourceName] = firestore
        .collection(firestorePrefix + collectionId)
        .doc(recordId)
        .onSnapshot({
          next: res => setRecord(convertRecord(res, properties, assetsProperties)),
          error: err => {
            toast(err)
            setRecord({})
          },
        })
    })

  const getRecords = (
    name: string,
    set: (records: DataCollectionRecord[]) => void,
    collectionId: string,
    queryString: string,
    limit?: number,
    ids?: string[]
  ) =>
    new Promise(async resolve => {
      const properties = getCollectionProperties(collections, collectionId)
      const assetsProperties = getCollectionProperties(collections, assetsId)
      let loaded = false
      const setRecords = async (records: DataCollectionRecord[]) => {
        await set(records)
        setTimeout(() => {
          if (loaded) {
            runRefreshes(name)
          }
          loaded = true
          resolve(null)
        }, 1)
      }
      const { filters, ordered } = (queryString ? JSON.parse(queryString) : { filters: [], ordered: [] }) as {
        filters: {
          fieldPath: string
          opStr: firebase.firestore.WhereFilterOp
          value: any
        }[]
        ordered: {
          fieldPath: string
          direction: firebase.firestore.OrderByDirection
        }[]
      }
      if (ids) {
        filters.push({
          fieldPath: 'id',
          opStr: TableQueryFilterOperator.in,
          value: ids,
        })
      }
      const apiCall = !!filters.find(({ fieldPath }) =>
        properties.find(({ name, type }) => name === fieldPath && type === ValueType.vector)
      )
      if (apiCall) {
        snapshots[name]?.()
        search(
          `collections/${firestorePrefix + collectionId}?query=${encodeURIComponent(
            JSON.stringify({
              where: {
                compositeFilter: {
                  op: 'AND',
                  filters: filters.map(({ fieldPath, opStr, value }) => ({
                    fieldFilter: {
                      field: { fieldPath },
                      op: queryOperators[opStr],
                      value: { [getValueType(properties, fieldPath)]: value },
                    },
                  })),
                },
              },
              orderBy: ordered.map(({ fieldPath, direction }) => ({
                field: { fieldPath },
                direction: direction === 'asc' ? 'ASCENDING' : 'DESCENDING',
              })),
              limit,
            })
          )}`
        )
          .then(res => setRecords(res.data))
          .catch(err => {
            toast(err)
            setRecords([])
          })
      } else {
        let collectionQuery: firebase.firestore.Query
        collectionQuery = firestore.collection(firestorePrefix + collectionId)
        filters.forEach(
          ({ fieldPath, opStr, value }) =>
            (collectionQuery = collectionQuery.where(
              fieldPath === 'id' ? firebase.firestore.FieldPath.documentId() : fieldPath,
              opStr,
              value
            ))
        )
        ordered.forEach(
          ({ fieldPath, direction }) =>
            (collectionQuery = collectionQuery.orderBy(
              fieldPath === 'id' ? firebase.firestore.FieldPath.documentId() : fieldPath,
              direction
            ))
        )
        if (limit) {
          collectionQuery = collectionQuery.limit(limit)
        }
        snapshots[name]?.()
        snapshots[name] = collectionQuery.onSnapshot(
          { includeMetadataChanges: true },
          {
            next: res =>
              !res.metadata.fromCache &&
              !res.metadata.hasPendingWrites &&
              setRecords(res.docs.map(el => convertRecord(el, properties, assetsProperties))),
            error: err => {
              if (err.code === 'failed-precondition') {
                const filterFields = [
                  ...filters
                    .filter(({ fieldPath }) => !ordered.find(el => el.fieldPath === fieldPath))
                    .map(({ fieldPath, opStr }) =>
                      opStr === operators.contains
                        ? { fieldPath, arrayConfig: 'CONTAINS' }
                        : { fieldPath, order: 'DESCENDING' }
                    ),
                  ...ordered.map(({ fieldPath, direction }) => ({
                    fieldPath,
                    order: direction === 'asc' ? 'ASCENDING' : 'DESCENDING',
                  })),
                ]
                window.parent.postMessage(
                  `${NEED_INDEX} ${JSON.stringify({
                    collectionId,
                    filterFields,
                  })}`,
                  '*'
                )
              }
              toast(err)
              setRecords([])
            },
          }
        )
      }
    })

  const update: Update = async (getVariableValue, action) => {
    const updateRecord = async (newRecord: DataCollectionRecord, source: VariableSource) => {
      const record = await getVariableValue({
        source: source.type === VariableSourceType.component ? source : { ...source, fieldName: undefined },
      })
      const { collectionId, recordId } = extractRecordRef(record)
      const properties = getCollectionProperties(collections, collectionId)
      const assetsProperties = getCollectionProperties(collections, assetsId)
      reconvertRecord(newRecord, properties, assetsProperties)
      await firestore
        .collection(firestorePrefix + collectionId)
        .doc(recordId)
        .update(newRecord)
    }
    const createRecord = async (
      newRecord: DataCollectionRecord,
      collection: CollectionVariable,
      valueTarget?: VariableSource
    ) => {
      const collectionId = await getCurrentCollectionId(collection, getVariableValue)
      const collectionData = collections.find(el => el.name === collection.name)
      if (collectionData) {
        const { properties = [] } = collectionData
        const assetsProperties = getCollectionProperties(collections, assetsId)
        const recordId = newRecord.id || generateFirestoreId()
        reconvertRecord(newRecord, properties, assetsProperties)
        const recordReference = firestore.collection(firestorePrefix + collectionId).doc(recordId)
        await recordReference.set(newRecord)
        if (valueTarget) {
          const set = async (record: DataCollectionRecord) => {
            const firebaseDatasourceName = `${collectionId}/${recordId}`
            firebaseDatasources[firebaseDatasourceName] = record
            const { fieldName, variableName } = valueTarget
            if (fieldName) {
              await updateRecord({ [fieldName]: record }, valueTarget)
            } else if (variableName) {
              const value = {
                ...collectionData,
                collectionId,
                records: [record],
              }
              await getVariableValue(
                {
                  source: {
                    type: VariableSourceType.localVariable,
                    variableName,
                  },
                },
                undefined,
                { value }
              )
            }
          }
          await getRecord(set, collectionId, recordId)
        }
      }
    }
    const deleteRecord = async (source: VariableSource) => {
      const record = await getVariableValue({ source })
      const { collectionId, recordId } = extractRecordRef(record)
      const recordReference = firestore.collection(firestorePrefix + collectionId).doc(recordId)
      await recordReference.delete()
    }
    const getNewRecord = async (recordModifications: RecordModification[]) => {
      const newRecord: DataCollectionRecord = {}
      for (const el of recordModifications) {
        const { fieldName, value } = el
        if (fieldName) {
          newRecord[fieldName] = await getVariableValue(value)
        }
      }
      return newRecord
    }
    const { valueTarget, collection, recordModifications, record, actionType } = action
    if (
      actionType === ActionType.getImage ||
      actionType === ActionType.getVideo ||
      actionType === ActionType.getAudio ||
      actionType === ActionType.getFile
    ) {
      return new Promise((resolve, reject) => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept =
          actionType === ActionType.getImage
            ? 'image/*'
            : actionType === ActionType.getVideo
              ? 'video/*'
              : actionType === ActionType.getAudio
                ? 'audio/*'
                : '*'
        input.click()
        let cancelled = true
        input.addEventListener('change', async (e: any) => {
          cancelled = false
          const file = e.target.files[0]
          if (file) {
            const newRecord = await getAssetRecord(getVariableValue, file)
            if (newRecord) {
              await createRecord(newRecord, { name: assetsId }, valueTarget)
              resolve()
            }
          }
          reject(new Error('Something went wrong!'))
        })
        const cancel = () => {
          input.removeEventListener('cancel', cancel)
          window.removeEventListener('focus', cancel)
          window.removeEventListener('touchstart', cancel)
          setTimeout(() => cancelled && reject(), 1000)
        }
        input.addEventListener('cancel', cancel)
        window.addEventListener('focus', cancel)
        window.addEventListener('touchstart', cancel)
      })
    } else if (actionType === ActionType.createRecord && recordModifications && collection) {
      const newRecord = await getNewRecord(recordModifications)
      await createRecord(newRecord, collection, valueTarget)
    } else if (actionType === ActionType.updateRecord && recordModifications && record) {
      const newRecord = await getNewRecord(recordModifications)
      await updateRecord(newRecord, record)
    } else if (actionType === ActionType.deleteRecord && record) {
      await deleteRecord(record)
    }
  }

  useEffect(() => {
    scrolls = {}
    chatsLimits = {}
    refreshes = {}
    throttles = {}
    variables = {}
    firebaseDatasources = {}
    Object.keys(snapshots).forEach(el => {
      snapshots[el]()
      delete snapshots[el]
    })
  }, [auth.currentUser])

  useEffect(() => {
    return () => {
      refreshes = {}
      throttles = {}
      if (screen) {
        deleteScreenComponentsVariables(screen.id)
      }
    }
  }, [inputParameterValue, searchParams, screen])

  return { getVariable, update }
}

export const collectionPrefix = 'collection://'

export const extractRecordRef = (recordRef: string) => {
  const segments = recordRef.replace(collectionPrefix, '').split('/')
  const collectionId = segments.slice(0, -1).join('/')
  const recordId = segments.slice(-1)[0]
  return { collectionId, recordId }
}

const getCurrentCollectionId = async (
  collection: CollectionVariable,
  getVariableValue: GetVariableValue,
  refresh?: Refresh
) => {
  const { collectionId, name, params } = collection
  if (collectionId) {
    return collectionId
  } else {
    let collectionId = name
    if (params) {
      for (const el of Object.keys(params)) {
        const param = params[el]
        const paramValue = await getVariableValue({ ...param, stringConstant: param.constant }, refresh)
        if (paramValue) {
          collectionId = collectionId.replace(`{${el}}`, paramValue)
        }
      }
    }
    return collectionId
  }
}

export const queryToString = async (name: string, getVariableValue: GetVariableValue, query?: TableQuery) => {
  const filters: {
    fieldPath: string
    opStr: firebase.firestore.WhereFilterOp
    value: any
  }[] = []
  const ordered: {
    fieldPath: string
    direction: firebase.firestore.OrderByDirection
  }[] = []
  if (query?.filters) {
    for (const el of query.filters) {
      const value = await getVariableValue(el.value, { [`${name}/${el.field}/variable`]: () => runRefreshes(name) })
      if (value !== '' && value !== null && value !== undefined) {
        const opStr = operators[el.operator]
        filters.push({ fieldPath: el.field, opStr, value })
        if (needOrderByOperators.includes(opStr)) {
          ordered.push({ fieldPath: el.field, direction: 'asc' })
        }
      }
    }
  }
  if (query?.ordered) {
    for (const el of query.ordered) {
      ordered.push({
        fieldPath: el.field,
        direction: el.order === TableQueryOrder.ascending ? 'asc' : 'desc',
      })
    }
  } else {
    ordered.push({ fieldPath: 'createdAt', direction: 'desc' })
  }
  return JSON.stringify({ filters, ordered })
}

const getParticipantFullName = async (
  participantId: string,
  participantNameKeys: string[],
  getVariableValue: GetVariableValue,
  refresh?: Refresh
) => {
  const fields: string[] = []
  for (const fieldName of participantNameKeys) {
    fields.push(
      await getVariableValue(
        {
          source: {
            type: VariableSourceType.collection,
            collection: { name: profilesId },
            selector: { constant: participantId },
            fieldName,
          },
        },
        refresh
      )
    )
  }
  return fields.join(' ')
}

const createAvatar = async (
  participantId: string,
  participantNameKeys: string[],
  getVariableValue: GetVariableValue,
  refresh?: Refresh
) => {
  const randomColor = () => '#' + (0x1000000 | (Math.random() * 0xffffff)).toString(16).substr(1, 6)
  const canvas = document.createElement('canvas')
  canvas.width = 100
  canvas.height = 100
  const context = canvas.getContext('2d')
  if (context) {
    context.fillStyle = randomColor()
    context.beginPath()
    context.ellipse(canvas.width / 2, canvas.height / 2, canvas.width / 2, canvas.height / 2, 0, 0, Math.PI * 2)
    context.fill()
    context.font = '60px serif'
    context.fillStyle = randomColor()
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    const initials = (await getParticipantFullName(participantId, participantNameKeys, getVariableValue, refresh))
      .split(' ')
      .map(el => el[0] || '')
      .join('')
    context.fillText(initials, canvas.width / 2, canvas.height / 2)
  }
  return canvas.toDataURL()
}

export const setInputParameter = async (
  id: string,
  getVariableValue: GetVariableValue,
  inputParameter: ScreenParameter,
  value: any
) => {
  const { parameter, type, collection } = inputParameter
  if (type === ValueType.record && collection) {
    const { collectionId, recordId } = extractRecordRef(value)
    const refresh = async () => {
      const value = await getVariableValue(
        {
          source: {
            type: VariableSourceType.collection,
            collection: { name: collection, collectionId },
            selector: { constant: recordId },
          },
        },
        { [generateRefreshKey(id, `${parameter}/parameter`)]: refresh },
        undefined,
        true
      )
      await getVariableValue(
        { source: { type: VariableSourceType.localVariable, variableName: parameter } },
        undefined,
        { value },
        true
      )
    }
    await refresh()
  } else {
    await getVariableValue(
      { source: { type: VariableSourceType.localVariable, variableName: parameter } },
      undefined,
      {
        value:
          value !== null && value !== undefined
            ? type === ValueType.number
              ? +value
              : type === ValueType.boolean
                ? value === 'true'
                : value
            : value,
      },
      true
    )
  }
}

export const setLocalVariables = async (
  id: string,
  getVariableValue: GetVariableValue,
  localVariables: LocalVariable[],
  indexInList?: number
) => {
  for (const el of localVariables) {
    const { name, variable } = el
    if (variable) {
      const refresh = async () => {
        const value = await getVariableValue(
          variable,
          { [generateRefreshKey(id, `${name}/variable`, indexInList)]: refresh },
          undefined,
          true
        )
        await getVariableValue(
          { source: { type: VariableSourceType.localVariable, variableName: name } },
          undefined,
          { value },
          true
        )
      }
      await refresh()
    } else {
      const value = null
      await getVariableValue(
        { source: { type: VariableSourceType.localVariable, variableName: name } },
        undefined,
        { value },
        true
      )
    }
  }
}

export const getParams = async (getVariableValue: GetVariableValue, parameters: InputParameter[]) => {
  const params: { [key: string]: any } = {}
  for (const el of parameters) {
    const { name, value } = el
    if (name) {
      params[name] = await getVariableValue(value)
    }
  }
  return params
}
